import React, { memo, useState, useEffect, useRef, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LabelList,
} from 'recharts';
import useSimulationStore from '../../../store/simulationStore';

interface ParticleCountChartProps {
    height?: number;
}

// More distinct color palette for species - higher contrast
const COLORS = [
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#FFE66D', // Yellow
    '#95E1D3', // Mint
    '#F38181', // Coral
    '#AA96DA', // Purple
    '#FCBAD3', // Pink
    '#A8D8EA', // Sky Blue
    '#FF9F43', // Orange
    '#5C7AEA', // Blue
];

const MAX_DATA_POINTS = 600; // Keep 5 minutes of data at 500ms intervals (rolling window)
const UPDATE_INTERVAL = 500; // Update every 0.5 seconds for smoother lines

interface DataPoint {
    time: number;
    [species: string]: number;
}

// Direct color mapping for common chemical species (more readable)
const SPECIES_COLORS: { [name: string]: string } = {
    'H2': '#4ECDC4',   // Teal
    'O2': '#FF6B6B',   // Red
    'H': '#FFE66D',    // Yellow
    'O': '#F38181',    // Coral
    'OH': '#95E1D3',   // Mint
    'H2O': '#5C7AEA',  // Blue
};

// Get color for species - use predefined if available, otherwise hash
const getColorForSpecies = (name: string): string => {
    if (SPECIES_COLORS[name]) {
        return SPECIES_COLORS[name];
    }
    // Fallback to hash-based color for unknown species
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return COLORS[Math.abs(hash) % COLORS.length];
};

const ParticleCountChart: React.FC<ParticleCountChartProps> = memo(({ height = 250 }) => {
    const [history, setHistory] = useState<DataPoint[]>([]);
    const [currentCounts, setCurrentCounts] = useState<{ [species: string]: number }>({});
    const timeRef = useRef(0);
    const lastResetCountRef = useRef(0);
    const previousCountsRef = useRef<{ [species: string]: number }>({});

    // Subscribe to projector mode for scaling
    const projectorMode = useSimulationStore(state => state.controls.projectorMode);
    const scale = projectorMode ? 1.5 : 1; // 1.5x fonts and lines in projector mode

    // Poll the store at our own pace instead of subscribing
    // This prevents the chart from causing re-renders at 60fps
    useEffect(() => {
        const pollInterval = setInterval(() => {
            const state = useSimulationStore.getState();
            const counts = state.speciesCounts;
            const resetCount = state.resetCount;

            // Check for reset
            if (resetCount !== lastResetCountRef.current) {
                lastResetCountRef.current = resetCount;
                setHistory([]);
                timeRef.current = 0;
                setCurrentCounts({});
                previousCountsRef.current = {};
                return;
            }

            // Check if simulation has active particles
            const totalParticles = Object.values(counts).reduce((sum, c) => sum + c, 0);
            if (totalParticles === 0) {
                return;
            }

            // Update current counts for pie chart (always update for real-time display)
            setCurrentCounts(counts);

            // Check if counts actually changed from last poll
            // Skip recording if simulation is lagging and data is stale
            const prevCounts = previousCountsRef.current;
            const hasChanged = Object.keys(counts).some(key => counts[key] !== prevCounts[key]) ||
                Object.keys(prevCounts).some(key => counts[key] !== prevCounts[key]);

            if (!hasChanged && Object.keys(prevCounts).length > 0) {
                // Data hasn't changed - simulation is probably lagged, skip this sample
                return;
            }

            // Save current counts for next comparison
            previousCountsRef.current = { ...counts };

            // Add to history for line chart
            timeRef.current += UPDATE_INTERVAL / 1000;
            setHistory(prev => {
                const newPoint: DataPoint = {
                    time: timeRef.current,
                    ...counts
                };
                const updated = [...prev, newPoint];
                return updated.length > MAX_DATA_POINTS
                    ? updated.slice(-MAX_DATA_POINTS)
                    : updated;
            });
        }, UPDATE_INTERVAL);

        return () => clearInterval(pollInterval);
    }, []); // Empty deps - never re-creates, just polls

    // Sort species names for stable ordering
    const speciesNames = useMemo(() => Object.keys(currentCounts).sort(), [currentCounts]);

    // Pie chart data with stable colors based on species name
    const pieData = useMemo(() => {
        return speciesNames.map(name => ({
            name,
            value: currentCounts[name] || 0,
            color: getColorForSpecies(name),
        }));
    }, [currentCounts, speciesNames]);

    // Total particles for display
    const totalParticles = useMemo(() =>
        Object.values(currentCounts).reduce((sum, count) => sum + count, 0),
        [currentCounts]
    );

    return (
        <div style={containerStyle}>
            {/* Summary bar */}
            <div style={{ ...summaryStyle, fontSize: `${14 * scale}px` }}>
                <span>Total: <strong style={{ color: '#4fc3f7' }}>{totalParticles}</strong></span>
                <span>Species: <strong style={{ color: '#81c784' }}>{speciesNames.length}</strong></span>
                <span>Time: <strong style={{ color: '#ffb74d' }}>{timeRef.current.toFixed(0)}s</strong></span>
            </div>

            {/* Main content: Pie (1x) + Line (2x) */}
            <div style={chartContainerStyle}>
                {/* Left: Pie Chart (A) */}
                <div style={pieContainerStyle}>
                    <div style={{ ...sectionTitleStyle, fontSize: `${14 * scale}px` }}>Current Ratios</div>
                    {pieData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="30%"
                                    outerRadius="70%"
                                    paddingAngle={2}
                                    dataKey="value"
                                    isAnimationActive={false}
                                    label={({ name, percent, cx, cy, midAngle, outerRadius }) => {
                                        // Only show label if slice is > 5% of total
                                        if (!percent || !midAngle || !name || percent < 0.05) return null;
                                        const RADIAN = Math.PI / 180;
                                        const radius = Number(outerRadius) * 1.15;
                                        const x = Number(cx) + radius * Math.cos(-midAngle * RADIAN);
                                        const y = Number(cy) + radius * Math.sin(-midAngle * RADIAN);
                                        return (
                                            <text
                                                x={x}
                                                y={y}
                                                fill={getColorForSpecies(String(name))}
                                                textAnchor={x > Number(cx) ? 'start' : 'end'}
                                                dominantBaseline="central"
                                                fontSize={12 * scale}
                                                fontWeight="bold"
                                            >
                                                {name}
                                            </text>
                                        );
                                    }}
                                    labelLine={false}
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    content={({ payload }) => {
                                        if (!payload || payload.length === 0) return null;
                                        const item = payload[0];
                                        const name = item.name as string;
                                        const value = item.value as number;
                                        const color = getColorForSpecies(name);
                                        const percent = totalParticles > 0 ? ((value / totalParticles) * 100).toFixed(1) : 0;
                                        return (
                                            <div style={{ ...tooltipStyle, padding: '8px 12px' }}>
                                                <span style={{ color, fontWeight: 'bold', fontSize: `${14 * scale}px` }}>{name}</span>
                                                <span style={{ color: '#fff', marginLeft: '8px', fontSize: `${14 * scale}px` }}>
                                                    {value} ({percent}%)
                                                </span>
                                            </div>
                                        );
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={emptyStateStyle}>No data</div>
                    )}

                    {/* Legend below pie */}
                    <div style={{ ...legendStyle, fontSize: `${14 * scale}px` }}>
                        {pieData.map((entry, index) => (
                            <div key={entry.name} style={legendItemStyle}>
                                <div style={{ ...legendColorStyle, backgroundColor: entry.color, width: `${10 * scale}px`, height: `${10 * scale}px` }} />
                                <span>{entry.name}: {entry.value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: Line Chart (BB - 2x width) */}
                <div style={lineContainerStyle}>
                    <div style={{ ...sectionTitleStyle, fontSize: `${14 * scale}px` }}>Counts Over Time</div>
                    {history.length > 1 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={history} margin={{ top: 10, right: 50, left: 10, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                <XAxis
                                    dataKey="time"
                                    stroke="#888"
                                    tick={{ fill: '#ccc', fontSize: 14 * scale }}
                                    minTickGap={projectorMode ? 60 : 40}
                                    tickFormatter={(v) => {
                                        if (v >= 60) return `${(v / 60).toFixed(1)}m`;
                                        return `${v.toFixed(0)}s`;
                                    }}
                                />
                                <YAxis
                                    stroke="#888"
                                    tick={{ fill: '#ccc', fontSize: 14 * scale }}
                                    allowDecimals={false}
                                />
                                <Tooltip
                                    contentStyle={{ ...tooltipStyle, fontSize: 14 * scale }}
                                    labelFormatter={(label) => `Time: ${Number(label).toFixed(1)}s`}
                                />
                                <Legend wrapperStyle={{ fontSize: `${14 * scale}px` }} />
                                {speciesNames.map((species) => (
                                    <Line
                                        key={species}
                                        type="monotone"
                                        dataKey={species}
                                        stroke={getColorForSpecies(species)}
                                        strokeWidth={2 * scale}
                                        dot={false}
                                        isAnimationActive={false}
                                    >
                                        <LabelList
                                            dataKey={species}
                                            position="right"
                                            content={({ x, y, index, value }) => {
                                                // Only show label on the last point
                                                if (index !== history.length - 1 || !x || !y) return null;
                                                return (
                                                    <text
                                                        x={Number(x) + 5}
                                                        y={Number(y)}
                                                        fill={getColorForSpecies(species)}
                                                        fontSize={12 * scale}
                                                        fontWeight="bold"
                                                        dominantBaseline="middle"
                                                    >
                                                        {species}
                                                    </text>
                                                );
                                            }}
                                        />
                                    </Line>
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={emptyStateStyle}>
                            <span>Collecting data...</span>
                            <span style={{ fontSize: `${14 * scale}px`, color: '#666' }}>Start simulation to see counts over time</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

ParticleCountChart.displayName = 'ParticleCountChart';

// Styles
const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
};

const summaryStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: 'rgba(40, 40, 40, 0.5)',
    borderRadius: '4px',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#ccc',
    fontFamily: 'monospace',
};

const chartContainerStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    gap: '12px',
    minHeight: 0,
};

const sectionTitleStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#4fc3f7',
    textAlign: 'center',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
};

const pieContainerStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
};

const lineContainerStyle: React.CSSProperties = {
    flex: 2, // 2x width
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
};

const legendStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'center',
    padding: '6px',
    fontSize: '14px',
    color: '#ccc',
};

const legendItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
};

const legendColorStyle: React.CSSProperties = {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
};

const tooltipStyle: React.CSSProperties = {
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    border: '1px solid #4fc3f7',
    borderRadius: '4px',
    color: '#fff',
};

const emptyStateStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#888',
    fontSize: '12px',
    gap: '8px',
};

export default ParticleCountChart;
